import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Flame, Handshake, XCircle, RefreshCw, Package, ShieldAlert, TrendingUp, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* DEAL overlay — the burner-phone customer network. NPC buyers reach out over
   time against your inventory; accept, haggle, or pass. Suspicion climbs per
   deal; push it too far and one of these "customers" is a badge. */

const fmt$ = (n) => `$${(n ?? 0).toLocaleString()}`;
const QUALITY_COLOR = { schwag: "#a1a1aa", mid: "#86efac", premium: "#c084fc", exotic: "#f0abfc" };

const Meter = ({ label, value, max, color, Icon }) => (
  <div className="flex-1 border border-white/10 bg-black/50 px-3 py-2">
    <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500 mb-1 flex items-center gap-1"><Icon className="w-3 h-3" style={{ color }} />{label}</p>
    <div className="h-1.5 bg-black/60 border border-white/10 overflow-hidden">
      <div className="h-full transition-all duration-700" style={{ width: `${Math.min(100, (value / max) * 100)}%`, backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
    <p className="font-mono text-[9px] mt-1" style={{ color }}>{value}{label === "Heat" ? "/100" : ""}</p>
  </div>
);

export default function DealCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [data, setData] = useState(null);
  const [inv, setInv] = useState([]);
  const [busy, setBusy] = useState("");
  const [counterFor, setCounterFor] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const sfx = usePhoneAudio();
  const pollRef = useRef(null);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const DEMO = {
    offers: [
      { id: "demo-o1", customer_name: "Noodle", suburb: "Route 68", strain_name: "Violet Static", quality: "exotic", grams: 7, price_g: 26.4, total: 184.8, expires_at: new Date(Date.now() + 3 * 60000).toISOString() },
      { id: "demo-o2", customer_name: "Big Sal", suburb: "The Docks", strain_name: "Crystal", quality: "premium", grams: 12, price_g: 41.3, total: 495.6, expires_at: new Date(Date.now() + 2 * 60000).toISOString() },
    ],
    suspicion: 34, rep: 11, customers: 12,
  };
  const DEMO_INV = [
    { key: "weed|purple_haze|exotic", product: "weed", strain_name: "Violet Static", quality: "exotic", grams: 44, street_price_g: 28.8 },
    { key: "meth|crystal|premium", product: "meth", strain_name: "Crystal", quality: "premium", grams: 18.5, street_price_g: 44.8 },
  ];

  const load = useCallback(async () => {
    if (demo) { setData((d) => d || DEMO); setInv((i) => i.length ? i : DEMO_INV); return; }
    try {
      const [o, s] = await Promise.all([
        api.get(`/nc/servers/${serverId}/drugs/offers`, { params: { token } }),
        api.get(`/nc/servers/${serverId}/drugs/state`, { params: { token } }),
      ]);
      setData(o.data);
      setInv(s.data.inventory || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [demo, serverId, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const offers = data?.offers || [];

  const act = async (offer, kind) => {
    if (busy) return;
    sfx.keyTick();
    setBusy(offer.id + kind);
    if (demo) {
      if (kind === "accept") {
        toast.success(`${offer.customer_name} handed over ${fmt$(offer.total)} (demo)`);
        setData((d) => ({ ...d, offers: d.offers.filter((o) => o.id !== offer.id), suspicion: Math.min(100, d.suspicion + 6), rep: d.rep + 2 }));
      } else if (kind === "decline") {
        setData((d) => ({ ...d, offers: d.offers.filter((o) => o.id !== offer.id) }));
      } else {
        toast.info("Haggling works live — demo keeps it simple");
      }
      setCounterFor(null);
      setBusy("");
      return;
    }
    try {
      let r;
      if (kind === "accept") r = await api.post(`/nc/servers/${serverId}/drugs/offers/${offer.id}/accept`, {}, { params: { token } });
      else if (kind === "counter") r = await api.post(`/nc/servers/${serverId}/drugs/offers/${offer.id}/counter`, { price_g: Number(counterPrice) }, { params: { token } });
      else r = await api.post(`/nc/servers/${serverId}/drugs/offers/${offer.id}/decline`, {}, { params: { token } });
      if (r.data.bust) { sfx.error?.(); toast.error(r.data.message); }
      else if (r.data.accepted === false) toast.error(r.data.message);
      else if (r.data.accepted) { sfx.success(); toast.success(r.data.message); }
      setCounterFor(null);
      setCounterPrice("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div data-testid="deal-page" className="fixed inset-0 overflow-hidden pointer-events-none flex items-center justify-center p-4"
      style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="dealer network" />}

      <div data-testid="deal-card" className="pointer-events-auto w-full max-w-md border border-emerald-500/30 bg-[#04120bf2] backdrop-blur-md"
        style={{ boxShadow: "0 0 80px #10b98114, 0 30px 60px #000000cc" }}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-500/25">
          <Handshake className="w-4 h-4 text-emerald-400" />
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-emerald-300">The network · {data?.customers ?? "…"} contacts</p>
          <button data-testid="deal-refresh" onClick={() => { sfx.tick?.(); load(); }}
            className="ml-auto p-1.5 border border-white/10 text-zinc-500 hover:text-zinc-200 transition-colors"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>

        {/* meters */}
        <div className="flex gap-2 px-4 pt-3">
          <Meter label="Rep" value={data?.rep ?? 0} max={100} color="#34d399" Icon={TrendingUp} />
          <Meter label="Heat" value={data?.suspicion ?? 0} max={100} color={(data?.suspicion ?? 0) > 60 ? "#ef4444" : "#f59e0b"} Icon={Flame} />
        </div>
        {(data?.suspicion ?? 0) > 60 && (
          <p data-testid="deal-heat-warning" className="mx-4 mt-2 font-mono text-[8px] uppercase tracking-[0.2em] text-red-400 border border-red-500/40 bg-red-500/10 px-3 py-1.5 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" /> You're hot — lay low a while or someone's going to be a badge
          </p>
        )}

        {/* inventory */}
        <div className="px-4 pt-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-1.5"><Package className="w-3 h-3" /> On hand</p>
          {inv.length === 0 ? (
            <p data-testid="deal-empty-inv" className="font-mono text-[10px] text-zinc-600 border border-dashed border-white/10 px-3 py-3 text-center">Nothing to move — grow it or cook it first</p>
          ) : (
            <div className="flex gap-1.5 flex-wrap">
              {inv.map((i) => (
                <span key={i.key} data-testid={`deal-inv-${i.key.replace(/\|/g, "-")}`} className="font-mono text-[9px] px-2.5 py-1.5 border border-white/15 bg-black/50"
                  style={{ color: QUALITY_COLOR[i.quality] }}>
                  {i.grams}g {i.strain_name} · {i.quality} · {fmt$(i.street_price_g)}/g
                </span>
              ))}
            </div>
          )}
        </div>

        {/* offers */}
        <div className="p-4 flex flex-col gap-2.5 max-h-[46vh] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">Incoming offers</p>
          {offers.length === 0 && (
            <p data-testid="deal-no-offers" className="font-mono text-[10px] text-zinc-600 border border-dashed border-white/10 px-3 py-6 text-center">
              Phone's quiet. Buyers surface every few minutes when you're holding product — check back.
            </p>
          )}
          {offers.map((o) => (
            <div key={o.id} data-testid={`deal-offer-${o.id.slice(0, 8)}`} className="border border-white/10 bg-black/50 p-3.5">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-emerald-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[11px] text-zinc-100">{o.customer_name} <span className="text-zinc-500">· {o.suburb}</span></p>
                  <p className="font-mono text-[9px]" style={{ color: QUALITY_COLOR[o.quality] }}>
                    wants {o.grams}g {o.strain_name} ({o.quality}) @ ${o.price_g}/g
                  </p>
                </div>
                <p className="font-mono text-sm font-bold text-emerald-300">{fmt$(o.total)}</p>
              </div>

              {counterFor === o.id ? (
                <div className="flex gap-2 mt-3">
                  <input data-testid={`deal-counter-input-${o.id.slice(0, 8)}`} type="number" min="1" step="0.5" value={counterPrice}
                    onChange={(e) => setCounterPrice(e.target.value)} placeholder={`counter $/g (they offered ${o.price_g})`}
                    className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-[10px] text-zinc-100 focus:outline-none focus:border-emerald-500/50" />
                  <button data-testid={`deal-counter-send-${o.id.slice(0, 8)}`} onClick={() => act(o, "counter")} disabled={!!busy || !counterPrice}
                    className="px-4 font-mono text-[9px] font-bold uppercase tracking-[0.15em] border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 transition-colors">Pitch it</button>
                  <button onClick={() => setCounterFor(null)} className="px-2 text-zinc-500 hover:text-zinc-300"><XCircle className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button data-testid={`deal-accept-${o.id.slice(0, 8)}`} onClick={() => act(o, "accept")} disabled={!!busy}
                    className="flex-1 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40"
                    style={{ backgroundColor: "#34d399" }}>Deal</button>
                  <button data-testid={`deal-counter-${o.id.slice(0, 8)}`} onClick={() => { setCounterFor(o.id); setCounterPrice(String(Math.round(o.price_g * 1.1 * 2) / 2)); }}
                    className="flex-1 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 text-zinc-300 hover:bg-white/5 transition-colors">Haggle</button>
                  <button data-testid={`deal-decline-${o.id.slice(0, 8)}`} onClick={() => act(o, "decline")} disabled={!!busy}
                    className="px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">Pass</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="px-4 pb-3 font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">
          Meets happen on their turf — heat climbs per swap. Exotic product pulls richer contacts.
        </p>
      </div>
    </div>
  );
}
